import { Component, ViewEncapsulation, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Price } from '@app/entities/price';
import { AppFacade } from '@app/app.facade';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { AirValidationResponse, AirValidationSector } from '@app/entities/air-validation.response';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TravelDetailProvider as AirTravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { PackageBookingDetailViewType } from '@packages/entities/booking-detail-view-type';
import { bookingInnerDetailRoute } from '@booking/constants/booking-routes.constants';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';

@Component({
  selector: 'app-mobile-package-booking-detail',
  templateUrl: './mobile-package-booking-detail.container.html',
  styleUrls: ['./mobile-package-booking-detail.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobilePackageBookingDetailComponent implements OnInit, OnDestroy {

  public passengerArrangement: PassengerArrangement[];
  public packagePrice: Price;
  public flightClass: FlightClass;
  public airValidationSector: AirValidationSector[];
  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public provider: string;
  public travelType: TravelBookingType;
  public hotelValidation: HotelValidationResponse;
  public airValidation: AirValidationResponse;
  public sectorSelected: number;
  public travelDetailProvider: AirTravelDetailProvider;
  public viewType: PackageBookingDetailViewType;
  public conversionRate: number = 0;
  public isInternational: boolean;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  constructor(
    private readonly facade: AppFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly domManipulateService: DomManipulateService,
    private readonly avoService: AvoService
  ) {
    const extras = this.router.getCurrentNavigation()?.extras;
    if (extras && extras.state) {
      this.passengerArrangement = extras.state.passengerArrangement;
      this.packagePrice = extras.state.packagePrice;
      this.flightClass = extras.state.flightClass;
      this.airValidationSector = extras.state.airValidationSector;
      this.provider = extras.state.provider;
      this.travelType = extras.state.travelType;
      this.sectorSelected = extras.state.sectorSelected;
      this.hotelValidation = extras.state.hotelValidation;
      this.airValidation = extras.state.airValidation;
      this.travelDetailProvider = extras.state.travelDetailProvider;
      this.viewType = extras.state.viewType;
      this.conversionRate = extras.state.conversionRate;
      this.isInternational = extras.state.isInternational;
    }
  }

  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.domManipulateService.setBookingScroll(false);
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.domManipulateService.setBookingScroll(true);
  }

  get hasValidators(): boolean {
    return !!this.hotelValidation && !!this.airValidationSector;
  }

  get packageBookingDetailViewType(): typeof PackageBookingDetailViewType {
    return PackageBookingDetailViewType;
  }

  private get eventSearchBy(): SearchBy {
    switch (this.viewType) {
      case PackageBookingDetailViewType.HOTEL:
        return SearchBy.HOTELS;
      case PackageBookingDetailViewType.AIR:
        return SearchBy.FLIGHTS;
      default:
        return SearchBy.PACKAGES;
    }
  }

  public goBack(): void {
    this.animation = ANIMATIONS.SLIDE_OUT_RIGHT;
    setTimeout(() => this.facade.navigateToLastRoute(), TIME_TO_SLIDE);
    this.domManipulateService.setBodyScroll(true);
    this.domManipulateService.setBookingScroll(true);
  }

  public showPackageHotelDetail(): void {
    this.router.navigate([{ outlets: { 'inner-detail': [bookingInnerDetailRoute] } }],
      {
        state: {
          passengerArrangement: this.passengerArrangement,
          packagePrice: this.packagePrice,
          flightClass: this.flightClass, provider: this.provider,
          airValidationSector: this.airValidationSector,
          viewType: PackageBookingDetailViewType.HOTEL,
          hotelValidation: this.hotelValidation,
          airValidation: this.airValidation,
          sectorSelected: this.sectorSelected,
          travelDetailProvider: this.travelDetailProvider
        },
        relativeTo: this.route, skipLocationChange: true
      }
    );
  }

  public showPackagAirDetail(sectorNumber: number): void {
    this.router.navigate([{ outlets: { 'inner-detail': [bookingInnerDetailRoute] } }],
      {
        state: {
          passengerArrangement: this.passengerArrangement,
          packagePrice: this.packagePrice,
          flightClass: this.flightClass, provider: this.provider,
          airValidationSector: this.airValidationSector,
          viewType: PackageBookingDetailViewType.AIR,
          hotelValidation: this.hotelValidation,
          airValidation: this.airValidation,
          sectorSelected: sectorNumber,
          travelDetailProvider: this.travelDetailProvider
        },
        relativeTo: this.route, skipLocationChange: true
      }
    );
  }
}
